/** フライトの物理（docs/03_simulation.md 8 章）。オートパイロットも手動操縦も同じ入力でこれを動かす */
import { FLIGHT } from '../../config/balance';
import type { FlightState } from '../types';

export interface ControlInput {
  brakeL: number;
  brakeR: number;
  bar: number;
  /** ビッグイヤー（翼端を折って沈下を増やす。着陸のための降下用）0〜1 */
  bigEars: number;
}

export interface AirSample {
  /** 水平の風（to 方向、m/s） */
  wx: number;
  wz: number;
  /** 上下の流れ（m/s） */
  w: number;
}

const G = 9.81;
const DEG = Math.PI / 180;

/** 速度入力 s（−0.85〜1）に対する対気速度と沈下 */
export function polarAt(s: number): { speedMs: number; sinkMs: number } {
  const pts = FLIGHT.polar;
  const first = pts[0] as (typeof pts)[number];
  const last = pts[pts.length - 1] as (typeof pts)[number];
  if (s <= first.s) return { speedMs: first.kmh / 3.6, sinkMs: first.sink };
  if (s >= last.s) return { speedMs: last.kmh / 3.6, sinkMs: last.sink };
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i] as (typeof pts)[number];
    const b = pts[i + 1] as (typeof pts)[number];
    if (s <= b.s) {
      const t = (s - a.s) / (b.s - a.s);
      return { speedMs: (a.kmh + (b.kmh - a.kmh) * t) / 3.6, sinkMs: a.sink + (b.sink - a.sink) * t };
    }
  }
  return { speedMs: last.kmh / 3.6, sinkMs: last.sink };
}

/** 通常速度・無風での滑空比 */
export function trimGlideRatio(sinkFactor: number): number {
  const p = polarAt(0);
  return (p.speedMs / p.sinkMs) * sinkFactor;
}

export function stepFlightPhysics(
  f: FlightState,
  input: ControlInput,
  air: AirSample,
  dt: number,
  sinkFactor: number,
): void {
  const k = 1 - Math.exp(-dt / FLIGHT.inputTauS);
  f.brakeL += (input.brakeL - f.brakeL) * k;
  f.brakeR += (input.brakeR - f.brakeR) * k;
  f.bar += (input.bar - f.bar) * k;
  const sym = Math.min(f.brakeL, f.brakeR);

  // 失速：ブレークの引きすぎ、または速度の不足
  if (!f.stall) {
    f.highBrakeS = sym > 0.85 ? f.highBrakeS + dt : 0;
    if (f.highBrakeS > 1) f.stall = { timerS: FLIGHT.stallRecoverS, deep: false };
  } else if (sym < 0.3) {
    f.stall.timerS -= dt;
    if (f.stall.timerS <= 0) {
      // 回復：翼が前に出て速度が戻る
      f.stall = null;
      f.highBrakeS = 0;
      f.airspeedMs = Math.max(f.airspeedMs, FLIGHT.stallKmh / 3.6 + 1.5);
    }
  }

  const s = Math.min(Math.max(f.bar - sym, -0.85), 1);
  const p = polarAt(s);
  const targetSpeed = f.stall ? 4 : p.speedMs;
  f.airspeedMs += (targetSpeed - f.airspeedMs) * (1 - Math.exp(-dt / FLIGHT.speedTauS));

  const asym = f.brakeR - f.brakeL;
  const bankTarget = f.stall
    ? 0
    : Math.min(
        Math.max(asym * FLIGHT.maxBankDeg * DEG, -FLIGHT.assistBankDeg * DEG),
        FLIGHT.assistBankDeg * DEG,
      );
  f.bankRad += (bankTarget - f.bankRad) * (1 - Math.exp(-dt / FLIGHT.bankTauS));
  f.headingRad += ((G * Math.tan(f.bankRad)) / Math.max(f.airspeedMs, 5)) * dt;

  const sink = f.stall
    ? f.stall.deep
      ? FLIGHT.deepStallSinkMs
      : FLIGHT.stallSinkMs
    : p.sinkMs / sinkFactor / Math.pow(Math.cos(f.bankRad), 1.5) + input.bigEars * FLIGHT.bigEarsSinkMs;
  const vy = air.w - sink;
  const vx = Math.sin(f.headingRad) * f.airspeedMs + air.wx;
  const vz = -Math.cos(f.headingRad) * f.airspeedMs + air.wz;
  f.pos.x += vx * dt;
  f.pos.y += vy * dt;
  f.pos.z += vz * dt;
  f.distanceM += Math.hypot(vx, vz) * dt;
  f.varioMs += (vy - f.varioMs) * (1 - Math.exp(-dt / 1));
  f.liftMs = air.w;
  f.maxAltM = Math.max(f.maxAltM, f.pos.y);
  f.circlingS = Math.abs(f.bankRad) > 18 * DEG ? f.circlingS + dt : Math.max(0, f.circlingS - dt * 2);
}
